/**
 * Article figure (G2, D6, R1/R2): figure > plate > frame > Media, + figcaption. The plate/frame
 * treatment (light: 1px frame; dark: the light plate) is drawn by the style block (figureRules).
 */
import React, { memo } from 'react';
import type { ClipStyle, FeaturedClipContent, FeaturedMedia } from '@segmently/schema-models/v2';
import { Media } from '@modules/screen-view-v2/components/shared/Media';
import type { ImageLoadingHints } from '@modules/screen-view-v2/components/shared/Image';
import type { ArticleVideoOptions } from '@modules/screen-view-v2/components/shared/Video/Video';
import type { PreviewMode } from '@modules/screen-view-v2/types/previewMode';
import { resolveLocaleText } from '@modules/screen-view-v2/utils/articleText';

const ABSOLUTE_HTTP = /^https?:\/\/\S+$/;
const PRELOADS = new Set<ArticleVideoOptions['preload']>(['none', 'metadata', 'auto']);

/** D6 defaults: controls on, no autoplay, preload none; poster = appearance.poster when absolute. */
export function resolveArticleVideoOptions(appearance: ClipStyle | undefined): ArticleVideoOptions {
  const poster = typeof appearance?.poster === 'string' && ABSOLUTE_HTTP.test(appearance.poster) ? appearance.poster : undefined;
  const preload = appearance?.preload && PRELOADS.has(appearance.preload) ? appearance.preload : 'none';
  return {
    controls: appearance?.controls ?? true,
    autoplay: appearance?.autoplay ?? false,
    preload,
    ...(poster ? { poster } : {}),
  };
}

/** G5 (R4): the lead figure (the screen's first Image) is fetched early; every other figure waits for the viewport. */
export const LEAD_FIGURE_HINTS: ImageLoadingHints = { loading: 'eager', decoding: 'async', fetchPriority: 'high' };
export const BODY_FIGURE_HINTS: ImageLoadingHints = { loading: 'lazy', decoding: 'async' };

export interface ArticleFigureProps {
  media: FeaturedMedia | undefined;
  locale: string;
  editPath?: string;
  previewMode?: PreviewMode;
  /** G5 (R4): this figure is the screen's lead figure (ArticleRenderContext.leadFigureSectionId). */
  lead?: boolean;
}

export const ArticleFigure = memo(function ArticleFigure({ media, locale, editPath, previewMode, lead = false }: ArticleFigureProps): React.ReactElement | null {
  if (!media) return null;
  const alt = resolveLocaleText(media.alt, locale);
  const caption = resolveLocaleText(media.caption, locale);
  const clip = media.kind === 'Video' ? (media.content as FeaturedClipContent).video : undefined;
  return (
    <figure className="ap-block ap-figure" data-article-figure="">
      <div className="ap-figure-plate">
        <div className="ap-figure-frame">
          <Media
            media={media}
            locale={locale}
            editPath={editPath}
            fillContainer
            previewMode={previewMode}
            alt={alt}
            videoOptions={clip ? resolveArticleVideoOptions(clip.appearance) : undefined}
            imageLoadingHints={lead ? LEAD_FIGURE_HINTS : BODY_FIGURE_HINTS}
          />
        </div>
      </div>
      {caption ? <figcaption className="ap-figure-caption">{caption}</figcaption> : null}
    </figure>
  );
});

ArticleFigure.displayName = 'ArticleFigure';
